/**
 * Syncs Skipp user location (city, lat, lng) to Shopify cart attributes via Ajax API.
 * Attributes appear on the order under Additional details.
 * Intercepts cart checkout submit to POST /cart/update.js when a city is set, then continue.
 */
import * as UL from './skipp-user-location.js';

const CHECKOUT_PASS_ATTR = 'data-skipp-checkout-pass';
const DEBOUNCE_MS = 400;

/**
 * @returns {{ attributes: Record<string, string> } | null}
 */
export function buildSkippCartLocationPayload() {
  const u = UL.getUserLocation();
  if (!u || !String(u.city || '').trim()) return null;
  const addr = String(u.fullAddress || u.label || '').trim();
  const pin = u.pincode ? String(u.pincode).trim() : '';
  return {
    attributes: {
      user_city: String(u.city).trim(),
      user_lat:
        u.lat != null && Number.isFinite(Number(u.lat)) ? String(Number(u.lat)) : '',
      user_lng:
        u.lng != null && Number.isFinite(Number(u.lng)) ? String(Number(u.lng)) : '',
      user_pincode: pin,
      user_address: addr,
    },
  };
}

/**
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export async function syncSkippUserLocationToCart() {
  const body = buildSkippCartLocationPayload();
  if (!body) return { ok: false, reason: 'no_location' };

  let existingAttrs = {};
  try {
    const cr = await fetch('/cart.js');
    if (cr.ok) {
      const cart = await cr.json();
      if (cart?.attributes && typeof cart.attributes === 'object') {
        existingAttrs = { ...cart.attributes };
      }
    }
  } catch {
    /* ignore — merge is best-effort */
  }

  const payload = {
    attributes: {
      ...existingAttrs,
      ...body.attributes,
    },
  };

  const res = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok) return { ok: false, reason: 'http' };
  return { ok: true };
}

function formActionIsCart(form) {
  try {
    const raw = (form.getAttribute('action') || '').trim();
    const path = new URL(raw || '/cart', window.location.origin).pathname;
    return /\/cart\/?$/.test(path);
  } catch {
    return false;
  }
}

function initCheckoutGate() {
  document.addEventListener(
    'submit',
    async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.hasAttribute(CHECKOUT_PASS_ATTR)) {
        form.removeAttribute(CHECKOUT_PASS_ATTR);
        return;
      }
      if (!formActionIsCart(form)) return;
      const sub = e.submitter;
      if (!sub || sub.name !== 'checkout') return;

      e.preventDefault();

      try {
        await syncSkippUserLocationToCart();
      } catch {
        /* best-effort — still proceed so checkout is not blocked by network blips */
      }

      form.setAttribute(CHECKOUT_PASS_ATTR, '');
      form.requestSubmit(sub);
    },
    true
  );
}

let debounceTimer = 0;
function scheduleSyncFromLocationEvent() {
  window.clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(() => {
    syncSkippUserLocationToCart().catch(() => {});
  }, DEBOUNCE_MS);
}

initCheckoutGate();
document.addEventListener('skipp:user-location-changed', scheduleSyncFromLocationEvent);
document.addEventListener('skipp:location-preference-changed', scheduleSyncFromLocationEvent);

/**
 * After Ajax add-to-cart, attach location to cart so checkout/Admin stay in sync (Dawn uses fetch, not pubsub in modules).
 */
function patchFetchForCartAddLocationSync() {
  if (window.__skippCartAddLocationPatched) return;
  window.__skippCartAddLocationPatched = true;
  const orig = window.fetch.bind(window);

  window.fetch = async function skippWrappedFetch(resource, init) {
    const response = await orig(resource, init);
    let url = '';
    try {
      if (typeof resource === 'string') url = resource;
      else if (resource instanceof Request) url = resource.url;
      else if (resource && typeof resource.url === 'string') url = resource.url;
    } catch {
      /* noop */
    }
    if (!url) return response;
    try {
      const path = new URL(url, window.location.origin).pathname;
      if (!/\/cart\/add(\.js)?$/i.test(path) || !response.ok) return response;
      response
        .clone()
        .json()
        .then((data) => {
          if (data && data.status) return;
          scheduleSyncFromLocationEvent();
        })
        .catch(() => {});
    } catch {
      /* noop */
    }
    return response;
  };
}

patchFetchForCartAddLocationSync();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    syncSkippUserLocationToCart().catch(() => {});
  });
} else {
  syncSkippUserLocationToCart().catch(() => {});
}

window.SkippCartLocationAttributes = {
  syncToCart: syncSkippUserLocationToCart,
  buildPayload: buildSkippCartLocationPayload,
};
